import React from "react"
import { TableCell, TableRow } from "@mui/material"
import FilterSelectAndAutocomplete from "./FormikSelectAndAutocomplete"
import { COLORS } from "../../utils"
import { PRODUCTION_STEP_EXECUTION_STATUSES } from "../../utils/productionStepExecution"

// Create consistent horizontal padding on both sides of the table
const BorderCell = () => <TableCell style={{ width: "56px" }} />

/**
 * FilterRowSelectAndAutocomplete
 *
 * Dynamically renders a table row containing filter inputs.
 * Each cell corresponds to a column that supports filtering.
 *
 * Props:
 * - filterFields: array of fields to filter (with key, placeholder, flags...)
 * - tableHeaders: array of table header configs (used to maintain column order)
 * - filters: current filter state object
 * - setFilters: state setter for updating filters
 * - values: Formik values (needed to compute unique options for fields)
 * - getUniqueOptions: helper to extract unique filter values from the available data
 */
const FilterRowSelectAndAutocomplete = ({ filterFields, tableHeaders, filters, setFilters, values, getUniqueOptions }) => {
  return (
    <TableRow style={{ backgroundColor: COLORS.PRIMARY_BLUE_20 }}>
      <BorderCell />

      {tableHeaders.map(({ key }) => {
        // Match this column key with a filter config if it exists
        const fieldConfig = filterFields.find(filterField => filterField.key === key)
        if (!fieldConfig) return <TableCell key={key} /> // Empty cell

        // Destructure filter config for this column
        const { placeholder, multiple, isStatus, isMachines, isPackagingLine, customOptions } = fieldConfig

        // Compute available options:
        // - Use customOptions if defined
        // - Otherwise, for "status" use the static status list
        // - Else, compute unique values based on data
        const options = customOptions
          || (isStatus
            ? PRODUCTION_STEP_EXECUTION_STATUSES.map(s => s.name)
            : getUniqueOptions(values, key, isMachines || isPackagingLine))

        return (
          <TableCell key={key} sx={{ verticalAlign: "bottom" }}>
            <FilterSelectAndAutocomplete
              value={filters[key]}
              onChange={value => {
                // Update filter state on change
                setFilters(prev => {
                  const updated = { ...prev }

                  // If cleared (empty or empty array), remove the filter
                  if (!value || (Array.isArray(value) && value.length === 0)) {
                    delete updated[key]
                  } else {
                    updated[key] = value
                  }

                  return updated
                })
              }}
              options={options}
              placeholder={placeholder}
              multiple={multiple || false}
            />
          </TableCell>
        )
      })}

      <BorderCell />
    </TableRow>
  )
}

export default FilterRowSelectAndAutocomplete